import { generatePrompt } from '@/services/openaiService';
import { useToast } from '@/hooks/use-toast';
import { promptHistoryService } from '@/services/promptHistoryService';
import { User } from '@/types/firebase';

interface GenerationResult {
  prompts: string[];
  error?: Error;
}

export class PromptGeneration {
  static async generateInitialPrompts(description: string, currentUser: User): Promise<GenerationResult> {
    try {
      const prompts = await Promise.all([
        generatePrompt(description, "Project Overview", "architecture planning"),
        generatePrompt(description, "Core Features", "feature implementation"),
        generatePrompt(description, "User Interface", "UI/UX design"),
        generatePrompt(description, "Technical Implementation", "development workflow"),
        generatePrompt(description, "Testing Strategy", "quality assurance")
      ]);

      // Save to history
      await promptHistoryService.savePrompt(currentUser.uid, {
        description,
        generatedPrompts: prompts,
        tokensUsed: 1,
        favorite: false
      });

      return { prompts };
    } catch (error: any) {
      console.error("Error generating prompts:", error);
      return { prompts: [], error };
    }
  }

  static async handleRegeneratePrompts(
    projectDescription: string, 
    additionalDescription: string, 
    setIsGenerating: (value: boolean) => void,
    setShowTypingEffect: (value: boolean) => void,
    setChatMessages: (updater: ((prev: any[]) => any[]) | any[]) => void,
    setGeneratedPrompts: (prompts: string[]) => void
  ) {
    if (!projectDescription.trim()) {
      return;
    }

    setIsGenerating(true);
    setShowTypingEffect(true);
    
    try {
      const combinedDescription = additionalDescription 
        ? `${projectDescription}. Additional details: ${additionalDescription}`
        : projectDescription;

      const enhancedPrompts = await Promise.all([
        generatePrompt(combinedDescription, "Enhanced Project Overview", "detailed architecture"),
        generatePrompt(combinedDescription, "Advanced Features", "comprehensive implementation"),
        generatePrompt(combinedDescription, "Polished UI/UX", "user experience optimization"),
        generatePrompt(combinedDescription, "Technical Excellence", "best practices"),
        generatePrompt(combinedDescription, "Quality Assurance", "testing and deployment")
      ]);
      
      setChatMessages(prev => [...prev, {
        type: 'ai', 
        content: `I've enhanced your prompts based on the additional details provided. These updated prompts should give you more specific guidance for building "${projectDescription.split(' ').slice(0, 5).join(' ')}..."`
      }]);
        
      setGeneratedPrompts(enhancedPrompts);
      setShowTypingEffect(false);
      
    } catch (error: any) {
      console.error("Error regenerating prompts:", error);
    } finally {
      setIsGenerating(false);
    }
  }

  static async regenerateSpecificPrompt(
    index: number,
    projectDescription: string,
    additionalDescription: string,
    generatedPrompts: string[],
    setGeneratedPrompts: (prompts: string[]) => void,
    setChatMessages: (updater: ((prev: any[]) => any[]) | any[]) => void,
    setIsGenerating: (value: boolean) => void
  ) {
    setIsGenerating(true);
    
    try {
      const promptType = ["Project Overview", "Core Features", "User Interface", "Technical Implementation", "Testing Strategy"][index];
      
      const regeneratedPrompt = await generatePrompt(
        `${projectDescription} ${additionalDescription}`,
        promptType,
        "enhanced detail and specificity"
      );
      
      const updatedPrompts = [...generatedPrompts];
      updatedPrompts[index] = regeneratedPrompt;
      
      setGeneratedPrompts(updatedPrompts);
      
      setChatMessages(prev => [...prev, {
        type: 'ai', 
        content: `I've enhanced prompt #${index + 1} with more specific implementation details and technical recommendations.`
      }]);
      
    } catch (error: any) {
      console.error("Error regenerating specific prompt:", error);
    } finally {
      setIsGenerating(false);
    }
  }
}
